// src/constants/pageSEO.js

import constants from "./constants";
import globalState from "../utils/globalState";

const getCurrentAgentName = () => constants.AGENT_NAME;

const pageSEO = {
  // home page SEO
  home: () => {
    const brokerageName = globalState.getCurrentBrokerage() || constants.BROKERAGE_NAME;
    const agentNumber = globalState.getCurrentAgentNumber() || constants.BROKERAGE_NUMBER;
    return {
      title: `${brokerageName} - Professional Real Estate Services`,
      description: `${brokerageName} - Professional real estate services to help you find your dream home. We offer property search, market analysis, and expert realtor services.`,
      keywords:
        "real estate, realtor, property, home, house, real estate services",
      path: "/",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: brokerageName,
        url:
          constants.ENVIRONMENT === "prod"
            ? `https://${constants.DOMAIN}`
            : `http://${constants.URL}`,
        logo: "/logo.png",
        contactPoint: {
          "@type": "ContactPoint",
          telephone: agentNumber,
          contactType: "customer service",
        },
      },
    };
  },

  // listings page SEO
  listings: () => ({
    title: "Property Listings | Find Your Dream Home",
    description:
      "Browse our curated selection of properties, including condos, single-family homes, and townhouses in Toronto and surrounding areas.",
    keywords:
      "property listings, real estate, homes for sale, Toronto properties, condos, houses",
    path: "/listings",
  }),

  // listing page SEO
  listing: (listing) => {
    const brokerageName = globalState.getCurrentBrokerage() || constants.BROKERAGE_NAME;
    return {
      // title: `${listing.address} - ${listing.location} | ${brokerageName}`,
      title: `${listing.StreetNumber} ${listing.StreetName} ${listing.StreetSuffix} - ${listing.City} ${listing.StateOrProvince} | ${getCurrentAgentName()}`,
      description: `${listing.bedrooms} bedroom ${listing.bathrooms} bathroom ${listing.propertyType} located in ${listing.location}, priced at ${listing.price}.`,
      keywords: `${listing.location}, ${listing.propertyType}, ${listing.bedrooms} bedroom, ${listing.bathrooms} bathroom, property`,
      path: `/listing/${listing.id}`,
      image: listing.mainImage,
      type: "product",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "RealEstateListing",
        name: listing.address,
        description: listing.description,
        image: listing.mainImage,
        offers: {
          "@type": "Offer",
          priceCurrency: "CAD",
          price: listing?.price?.replace(/[^\d.]/g, ""),
          availability: "https://schema.org/InStock",
        },
      },
    };
  },

  // about page SEO
  about: () => {
    const brokerageName = globalState.getCurrentBrokerage() || constants.BROKERAGE_NAME;
    const about = globalState.getCurrentAbout() || constants.ABOUT;
    return {
      title: `About ${brokerageName} | Our Story`,
      description: `Learn about ${brokerageName}'s commitment to excellence in real estate. ${about}`,
      keywords: `${brokerageName}, about us, real estate company, realtor team, real estate professionals`,
      path: "/about",
    };
  },

  // location page SEO
  location: (location) => {
    const brokerageName = globalState.getCurrentBrokerage() || constants.BROKERAGE_NAME;
    return {
      title: `${location} Real Estate and Properties | ${brokerageName}`,
      description: `Browse the latest properties, real estate market trends, and community information in ${location.name}. Learn about housing prices, schools, and local amenities in ${location.name}.`,
      keywords: `${location.name}, real estate, properties, homes for sale, ${location.name} community, ${location.name} housing market`,
      path: `/location/${location.slug}`,
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Place",
        name: location.name,
        description: `Browse the latest properties, real estate market trends, and community information in ${location.name}.`,
      },
    };
  },

  // agents page SEO
  agents: () => ({
    title: "Our Real Estate Agents",
    description:
      "Meet our team of professional real estate agents. Each agent brings extensive experience and expertise to help you buy or sell your property.",
    keywords:
      "real estate agents, realtors, property experts, real estate professionals",
    path: "/agents",
  }),

  // agent page SEO
  agent: (agent) => ({
    title: `${agent.name} - ${agent.title}`,
    description: `Meet ${agent.name}, our professional ${agent.title}. ${
      agent.bio ? agent.bio.substring(0, 150) + "..." : ""
    }`,
    keywords: `${agent.name}, real estate agent, realtor, property expert`,
    path: `/agents/${agent.name.toLowerCase().replace(/\s+/g, "-")}`,
    image: agent.img,
    type: "profile",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: agent.name,
      image: agent.img,
      description: agent.bio,
      telephone: agent.number,
      email: agent.email,
      worksFor: {
        "@type": "RealEstateOrganization",
        name: globalState.getCurrentBrokerage() || constants.BROKERAGE_NAME,
      },
    },
  }),

  // contact page SEO
  contact: () => {
    const brokerageName = globalState.getCurrentBrokerage() || constants.BROKERAGE_NAME;
    return {
      title: `Contact ${brokerageName} | Get in Touch`,
      description: `Contact the real estate team at ${brokerageName} for professional advice on buying, selling, or investing in real estate.`,
      keywords: "contact, real estate, agent, realtor, property consultation",
      path: "/contact",
    };
  },

  // evaluation page SEO
  evaluation: () => ({
    title: "Free Property Evaluation | Get Your Home's Value",
    description:
      "Get a free market evaluation of your property. Understand your property's value in the current market and make informed buying or selling decisions.",
    keywords:
      "property evaluation, home value, market analysis, real estate assessment",
    path: "/evaluation",
  }),

  // calculator page SEO
  calculator: () => ({
    title: "Mortgage Calculator | Estimate Your Payments",
    description:
      "Use our mortgage calculator to estimate your monthly payments, total interest costs, and affordability. Make informed financial decisions.",
    keywords:
      "mortgage calculator, loan calculator, monthly payments, interest, home buying budget",
    path: "/calculator",
  }),

  // reports page SEO
  reports: () => ({
    title: "Real Estate Market Reports | Latest Trends",
    description:
      "Latest real estate market trends, analysis, and forecasts. Learn about housing price trends, inventory levels, and market dynamics in different areas.",
    keywords:
      "real estate market, market reports, price trends, market analysis, real estate forecast",
    path: "/real-estate-reports",
  }),

  // commercial page SEO
  commercial: () => ({
    title: "Commercial Real Estate | Business Properties",
    description:
      "Browse our commercial property listings, including office spaces, retail storefronts, and industrial properties. Professional commercial real estate services.",
    keywords:
      "commercial real estate, office space, retail space, investment property, commercial property",
    path: "/commercial",
  }),

  // seller guide page SEO
  sellerGuide: () => ({
    title: "Seller's Guide | How to Sell Your Home",
    description:
      "Professional guide to selling your home, providing tips on property preparation, pricing strategy, and marketing techniques to get the best price.",
    keywords:
      "home selling, seller's guide, property sales, real estate market, pricing",
    path: "/selling-guide",
  }),

  // buyer guide page SEO
  buyerGuide: () => ({
    title: "Buyer's Guide | How to Buy a Home",
    description:
      "Comprehensive guide to buying a home, helping you understand the buying process, financing options, and factors to consider.",
    keywords:
      "home buying, buyer's guide, real estate, buying process, mortgage",
    path: "/buying-guide",
  }),
};

export default pageSEO;
